import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getPOs, getPO, createPO, updatePO, deletePO, getPOEditability, getPORevisions, confirmPO, acceptDelivery,
  getPaymentSchedule, getPaymentTerms, getPaymentTerm, forceInvoice, createUnbilledEntry,
  getPOBalance, approvePO, processDueInstallments, syncSoBridge,
  uploadAttachment, getAttachments, downloadAttachment,
  listSoModificationsForPo, approveSoModification, executeSoModification, resolveSoModificationGst,
  cancelSoModification,
  getRevenueSchedules, getRevenueSchedule, postRevenueEntry,
} from '../services/api';
import { formatCurrency, formatCurrencyDual, formatDate, formatDateTime, StatusBadge, TERM_TYPES, dispositionTaxableAmount, creditNoteTaxableAmount } from '../utils/helpers';
import { InstallmentEditor, validateInstallmentPct } from '../components/paymentTerms/InstallmentEditor';
import { Plus, ChevronRight, Check, Truck, CreditCard, ArrowRight, Zap, AlertTriangle, FileText, Download, Upload, X, Pencil, Trash2, CheckCircle2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { AppModal, ConfirmModal } from '../components/common/AppModal';
import SoModificationWizard from '../components/SoModificationWizard';
import { TermFormModal } from './PaymentTerms';
import { useAuth } from '../contexts/AuthContext';

/** Prefer SO-mod equal-monthly schedule over legacy invoice line schedules on the same PO. */
function pickPrimaryRevenueSchedule(schedules) {
  const actives = (schedules || []).filter(s => s.status === 'ACTIVE');
  if (!actives.length) return null;
  return actives.sort((a, b) => {
    const methodRank = s => (s.apportionment_method === 'EQUAL_MONTHLY' ? 2 : 0);
    if (methodRank(b) !== methodRank(a)) return methodRank(b) - methodRank(a);
    const total = s => parseFloat(s.total_amount || 0);
    if (total(b) !== total(a)) return total(b) - total(a);
    const entries = s => parseInt(s.pending_entries || 0, 10) + parseInt(s.posted_entries || 0, 10);
    if (entries(b) !== entries(a)) return entries(b) - entries(a);
    return (b.service_period_from || '').localeCompare(a.service_period_from || '');
  })[0];
}

/** Hide auto SO-mod orphan schedules until accounting is posted or explicitly invoice/unbilled-linked. */
function shouldShowRevenueSchedule(schedule, editability) {
  if (!schedule?.entries?.length && !schedule?.schedule_id) return false;
  if (editability?.catchup_applicable) return true;
  if (schedule.should_display === true) return true;
  if (schedule.should_display === false) return false;
  if (parseInt(schedule.posted_entries || 0, 10) > 0) return true;
  if (schedule.unbilled_id) return true;
  if (schedule.invoice_number || schedule.invoice_id) return true;
  if ((schedule.entries || []).some(e => e.status === 'POSTED')) return true;
  if ((schedule.entries || []).some(e => parseFloat(e.catchup_adjustment_amount || 0) > 0.009)) {
    return true;
  }
  const desc = String(schedule.line_description || '').trim();
  if (desc.startsWith('SO modification revenue schedule')) return false;
  return false;
}

// ── LIST ──────────────────────────────────────────────────────────
export function PurchaseOrderList() {
  const [pos, setPOs]               = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch]         = useState('');
  const navigate = useNavigate();
  const { canCreate } = useAuth();

  const load = useCallback(() => {
    getPOs().then(r => setPOs(r.data.purchase_orders || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? pos.filter(po =>
        (po.po_number || '').toLowerCase().includes(q) ||
        (po.title || '').toLowerCase().includes(q) ||
        (po.buyer_name || '').toLowerCase().includes(q) ||
        (po.status || '').toLowerCase().includes(q))
    : pos;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold">Sales Orders</h2>
        <div className="flex items-center gap-3">
          <div className="relative w-56 shrink-0">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
            />
            <input
              className="input input-with-icon w-full"
              placeholder="Search SO #, title, customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {canCreate && (
            <button onClick={() => setShowCreate(true)} className="btn-primary">
              <Plus size={16} /> New Sales Order
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <CreatePOModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      <div className="card overflow-hidden">
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 140 }}/>  {/* SO Number */}
            <col/>                          {/* Title — flexible */}
            <col style={{ width: 160 }}/>  {/* Customer */}
            <col style={{ width: 130 }}/>  {/* Value */}
            <col style={{ width: 100 }}/>  {/* Status */}
            <col style={{ width: 100 }}/>  {/* Date */}
            <col style={{ width: 36 }}/>   {/* Arrow */}
          </colgroup>
          <thead>
            <tr>
              {['SO Number', 'Title', 'Customer', 'Value', 'Status', 'Date', ''].map(h => (
                <th key={h} className="table-th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-td text-center text-gray-400">Loading…</td></tr>
            ) : pos.length === 0 ? (
              <tr><td colSpan={7} className="table-td text-center py-10 text-gray-400">No sales orders yet</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="table-td text-center py-10 text-gray-400">No sales orders match your search</td></tr>
            ) : filtered.map(po => (
              <tr key={po.po_id} className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/purchase-orders/${po.po_id}`)}>
                <td className="table-td font-mono text-primary-600 text-sm">{po.po_number}</td>
                <td className="table-td font-medium">{po.title}</td>
                <td className="table-td text-gray-500 text-sm">{po.buyer_name || '—'}</td>
                <td className="table-td font-semibold">{formatCurrency(po.po_value, po.currency)}</td>
                <td className="table-td"><StatusBadge status={po.status} /></td>
                <td className="table-td text-gray-500 text-sm">{formatDate(po.created_at)}</td>
                <td className="table-td"><ChevronRight size={16} className="text-gray-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── PENDING SO MODIFICATION ───────────────────────────────────────
function PendingSoModificationPanel({ modification, po, canApprove, canExecute, onRefresh, onNavigateToNewPo }) {
  const [loading, setLoading] = useState(null);
  const snapshot = typeof modification.impact_summary_snapshot === 'string'
    ? JSON.parse(modification.impact_summary_snapshot)
    : (modification.impact_summary_snapshot || {});
  const dispositionRows = (snapshot.disposition || []).filter(d => d.action_taken !== 'CARRIED_FORWARD');
  const gstBlocked = snapshot.gst_manual_review_required && !modification.gst_manually_resolved;
  const newValue = snapshot.new_po_value ?? snapshot.billing?.new_po_value;
  const alreadyBilled = snapshot.already_billed?.already_billed ?? snapshot.already_billed;

  const handleApprove = async () => {
    setLoading('approve');
    try {
      await approveSoModification(modification.modification_id);
      toast.success('Modification approved — ready to execute');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve modification');
    } finally {
      setLoading(null);
    }
  };

  const handleExecute = async () => {
    setLoading('execute');
    try {
      const res = await executeSoModification(modification.modification_id);
      toast.success(res.data.message || 'SO modification executed — credit notes synced to O2C');
      const newPoId = res.data.new_po?.po_id;
      if (newPoId) onNavigateToNewPo(newPoId);
      else onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to execute modification');
    } finally {
      setLoading(null);
    }
  };

  const handleResolveGst = async () => {
    setLoading('gst');
    try {
      await resolveSoModificationGst(modification.modification_id);
      toast.success('GST review resolved');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resolve GST review');
    } finally {
      setLoading(null);
    }
  };

  const handleDiscardDraft = async () => {
    setLoading('discard');
    try {
      await cancelSoModification(modification.modification_id);
      toast.success('Draft modification discarded');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to discard draft');
    } finally {
      setLoading(null);
    }
  };

  const statusLabel = {
    DRAFT: 'Draft',
    PENDING_APPROVAL: 'Pending Head Accounts approval',
    APPROVED: 'Approved — ready to execute',
  }[modification.status] || modification.status;

  return (
    <div className="text-sm bg-orange-50 border border-orange-200 rounded-xl px-4 py-4 space-y-3">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-semibold text-orange-900 flex items-center gap-2">
            <AlertTriangle size={16} />
            SO modification in progress
          </div>
          <p className="text-orange-800 mt-1">
            {statusLabel}. Credit notes, invoice adjustments, and O2C sync apply only after
            Head Accounts approval and execution.
          </p>
          {modification.reason && (
            <p className="text-orange-700 mt-1 text-xs">Reason: {modification.reason}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {modification.status === 'DRAFT' && (
            <button type="button" className="btn-secondary text-sm" disabled={!!loading}
              onClick={handleDiscardDraft}>
              {loading === 'discard' ? 'Discarding…' : 'Discard draft'}
            </button>
          )}
          {gstBlocked && canApprove && (
            <button type="button" className="btn-secondary text-sm" disabled={!!loading}
              onClick={handleResolveGst}>
              {loading === 'gst' ? 'Resolving…' : 'Resolve GST review'}
            </button>
          )}
          {modification.status === 'PENDING_APPROVAL' && canApprove && !gstBlocked && (
            <button type="button" className="btn-primary text-sm" disabled={!!loading}
              onClick={handleApprove}>
              {loading === 'approve' ? 'Approving…' : 'Approve modification'}
            </button>
          )}
          {modification.status === 'APPROVED' && canExecute && (
            <button type="button" className="btn-primary text-sm" disabled={!!loading}
              onClick={handleExecute}>
              {loading === 'execute' ? 'Executing…' : 'Execute modification'}
            </button>
          )}
        </div>
      </div>

      {(newValue != null || alreadyBilled != null) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          {alreadyBilled != null && (
            <div className="bg-white/70 rounded-lg px-3 py-2 border border-orange-100">
              <div className="text-orange-600">Already billed</div>
              <div className="font-semibold text-orange-900">{formatCurrency(alreadyBilled, po.currency)}</div>
            </div>
          )}
          {newValue != null && (
            <div className="bg-white/70 rounded-lg px-3 py-2 border border-orange-100">
              <div className="text-orange-600">New SO value</div>
              <div className="font-semibold text-orange-900">{formatCurrency(newValue, po.currency)}</div>
            </div>
          )}
        </div>
      )}

      {dispositionRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-orange-700 border-b border-orange-200">
                <th className="py-1 pr-3">Artifact</th>
                <th className="py-1 pr-3">Action</th>
                <th className="py-1">Amount</th>
              </tr>
            </thead>
            <tbody>
              {dispositionRows.map((row, i) => (
                <tr key={i} className="border-b border-orange-100/80">
                  <td className="py-1.5 pr-3">{row.artifact_type}</td>
                  <td className="py-1.5 pr-3 font-medium">{row.action_taken}</td>
                  <td className="py-1.5">{formatCurrency(dispositionTaxableAmount(row), po.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!canApprove && modification.status === 'PENDING_APPROVAL' && (
        <p className="text-xs text-orange-700">
          Head Accounts must approve this modification before credit notes can be issued.
        </p>
      )}
      {modification.status === 'APPROVED' && !canExecute && (
        <p className="text-xs text-orange-700">
          Approved — a Head Accounts or Sales Officer user must execute to create credit notes and update O2C.
        </p>
      )}
    </div>
  );
}

// ── DETAIL ────────────────────────────────────────────────────────
export function PurchaseOrderDetail() {
  const { id } = useParams();
  const [po, setPO]                 = useState(null);
  const [schedule, setSchedule]     = useState(null);
  const [terms, setTerms]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAddTerm, setShowAddTerm] = useState(false);
  const [editability, setEditability]   = useState(null);
  const [revisions, setRevisions]     = useState(null);
  const [showEdit, setShowEdit]       = useState(false);
  const [showRevise, setShowRevise]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [modifications, setModifications] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [revenueSchedule, setRevenueSchedule] = useState(null);
  const navigate = useNavigate();
  const { canApproveOrder, canCreate, canRaiseInvoice, isHeadAccounts, isAdmin, isFinance, isSalesOfficer } = useAuth();

  const load = useCallback(async () => {
    const [poRes, schedRes, termsRes, editRes, revRes, modRes] = await Promise.all([
      getPO(id),
      getPaymentSchedule(id).catch(() => null),
      getPaymentTerms({ po_id: id }).catch(() => null),
      getPOEditability(id).catch(() => ({ data: { editable: false } })),
      getPORevisions(id).catch(() => null),
      listSoModificationsForPo(id).catch(() => ({ data: { modifications: [] } })),
    ]);
    setPO(poRes.data.po);
    setCreditNotes(poRes.data.credit_notes || []);
    setSchedule(schedRes?.data || null);
    setTerms(termsRes?.data?.terms || []);
    setEditability(editRes?.data || null);
    setRevisions(revRes?.data || null);
    setModifications(modRes?.data?.modifications || []);
    try {
      const schedList = await getRevenueSchedules({ po_id: id });
      const primary = pickPrimaryRevenueSchedule(schedList?.data?.schedules || []);
      if (primary?.schedule_id) {
        const detail = await getRevenueSchedule(primary.schedule_id);
        const merged = { ...primary, ...(detail?.data?.schedule || {}) };
        setRevenueSchedule(
          shouldShowRevenueSchedule(merged, editRes?.data) ? merged : null
        );
      } else {
        setRevenueSchedule(null);
      }
    } catch {
      setRevenueSchedule(null);
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    try { await approvePO(id); toast.success('SO approved — SO Confirmation invoices raised'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to approve Sales Order'); }
  };

  const handleDelivery = async () => {
    try { await acceptDelivery(id, {}); toast.success('Delivery accepted — payment triggers fired'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deletePO(id);
      toast.success('Sales Order removed');
      navigate('/purchase-orders');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete Sales Order');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-48 bg-gray-200 rounded-xl"/><div className="h-64 bg-gray-200 rounded-xl"/></div>;
  if (!po) return <div className="text-center text-gray-500 py-12">Sales Order not found</div>;

  const hasTerm    = terms.length > 0;
  const hasSchedule = schedule?.installments?.length > 0;
  const canEditInPlace = editability?.in_place_edit_allowed && canCreate;
  const activeModification = modifications.find(
    m => ['DRAFT', 'PENDING_APPROVAL', 'APPROVED'].includes(m.status) && !m.executed_at
  );
  const showModifySoBtn = canCreate
    && editability?.modify_so_allowed
    && !['DRAFT', 'PENDING'].includes(po.status)
    && !activeModification;

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <button onClick={() => navigate('/purchase-orders')} className="hover:text-primary-600">
            Sales Orders
          </button>
          <ChevronRight size={14} />
          <span className="text-gray-900 font-medium">{po.po_number}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {showModifySoBtn && (
            <button onClick={() => setShowRevise(true)} className="btn-primary text-sm">
              <Pencil size={14} /> Modify Sales Order
            </button>
          )}
          {canEditInPlace && (
            <>
              <button onClick={() => setShowEdit(true)} className="btn-secondary text-sm">
                <Pencil size={14} /> {hasTerm ? 'Edit SO Setup' : 'Edit SO'}
              </button>
              <button onClick={() => setShowDeleteConfirm(true)} className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 size={14} /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {canEditInPlace && ['DRAFT', 'PENDING'].includes(po.status) && (
        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
          Pre-approval setup only — edit title, value, and payment terms before you approve this SO.
          After approval, use <strong>Modify Sales Order</strong> for any commercial change.
        </div>
      )}

      {editability?.partial_edit && editability?.reason && !showModifySoBtn && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          {editability.reason}
          {editability.scenario != null && (
            <span className="text-amber-600"> (posting scenario {editability.scenario})</span>
          )}
        </div>
      )}

      {activeModification && (
        <PendingSoModificationPanel
          modification={activeModification}
          po={po}
          canApprove={isHeadAccounts || isAdmin || isFinance}
          canExecute={isHeadAccounts || isAdmin || isFinance || isSalesOfficer}
          onRefresh={load}
          onNavigateToNewPo={(newPoId) => navigate(`/purchase-orders/${newPoId}`)}
        />
      )}

      {creditNotes.length > 0 && (
        <SoFamilyCreditNotesCard creditNotes={creditNotes} currency={po.currency} />
      )}

      {/* SO Header */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold">{po.po_number}</h1>
            <p className="text-gray-500 mt-1">{po.title}</p>
            {po.buyer_name && (
              <p className="text-sm text-gray-400 mt-0.5">Customer: <strong className="text-gray-600">{po.buyer_name}</strong></p>
            )}
          </div>
          <StatusBadge status={po.status} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
          {[
            { label: 'SO Value',     value: formatCurrencyDual(po.po_value, po.currency, po.conversion_rate) },
            { label: 'Currency',     value: po.currency },
            { label: 'Seller Org',   value: po.seller_org_name || '—' },
            { label: 'SO Date',      value: formatDate(po.po_date || po.created_at) },
            { label: 'Delivery Date',value: formatDate(po.delivery_date) },
            { label: 'Confirmed',    value: formatDateTime(po.confirmed_at) },
            { label: 'Invoice Mode', value: po.invoice_approval_mode?.replace(/_/g, ' ') || 'Platform Default' },
            { label: 'Version',      value: po.version },
          ].map(f => (
            <div key={f.label}>
              <div className="text-xs text-gray-400">{f.label}</div>
              <div className="font-medium mt-0.5">{f.value || '—'}</div>
            </div>
          ))}
        </div>

        <SOCopyPanel po={po} onUpdated={load} canUpload={canCreate} />

        {revisions?.versions?.length > 1 && (
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="text-sm font-semibold text-gray-700 mb-3">SO Version History</div>
            <div className="space-y-2">
              {revisions.versions.map(v => (
                <div key={v.po_id}
                  className={`flex items-center justify-between text-sm p-2 rounded-lg ${
                    v.po_id === id ? 'bg-primary-50 border border-primary-200' : 'bg-gray-50'
                  }`}>
                  <div>
                    <span className="font-medium">{v.po_number}</span>
                    <span className="text-gray-400 ml-2">v{v.version || 1}</span>
                    <StatusBadge status={v.status} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {v.approved_invoices > 0 && <span>{v.approved_invoices} invoice(s)</span>}
                    {v.po_id !== id ? (
                      <button type="button" className="text-primary-600 hover:underline"
                        onClick={() => navigate(`/purchase-orders/${v.po_id}`)}>
                        View
                      </button>
                    ) : (
                      <span className="text-primary-600 font-medium">Current</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {revisions.revision_log?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Audit Log</div>
                {revisions.revision_log.map(r => (
                  <div key={r.revision_id} className="text-xs text-gray-600 py-1">
                    <span className="text-gray-400">{formatDateTime(r.created_at)}</span>
                    {' — '}{r.change_comment}
                    {r.changed_by_name && <span className="text-gray-400"> ({r.changed_by_name})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SO Workflow Steps */}
        {!['COMPLETED','CANCELLED'].includes(po.status) && (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
              SO Lifecycle — Next Steps
            </div>
            <div className="grid grid-cols-3 divide-x divide-gray-200">

              {/* Step 1: Approve SO */}
              <div className={`p-4 ${['PENDING','DRAFT'].includes(po.status) ? 'bg-amber-50' : 'bg-white'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    ['PENDING','DRAFT'].includes(po.status) ? 'bg-amber-500 text-white' :
                    ['APPROVED','CONFIRMED','ACTIVE','COMPLETED'].includes(po.status) ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-400'
                  }`}>
                    {['APPROVED','CONFIRMED','ACTIVE','COMPLETED'].includes(po.status) ? '✓' : '1'}
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Approve Sales Order</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Approve this Sales Order to accept the contract. Any <strong>SO Confirmation</strong> installments will auto-raise invoices immediately.
                </p>
                {['PENDING','DRAFT'].includes(po.status) ? (
                  !canApproveOrder ? (
                    <div className="text-xs bg-gray-100 text-gray-500 border border-gray-200 rounded-lg p-2">
                      🔒 Only Sales Officers can approve Sales Orders
                    </div>
                  ) : hasTerm ? (
                    <button onClick={handleApprove}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors">
                      <Check size={14}/> Approve Sales Order
                    </button>
                  ) : (
                    <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      ⚠ Add payment terms below before you can approve this Sales Order
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
                    <Check size={12}/> Approved {po.confirmed_at ? formatDate(po.confirmed_at) : ''}
                  </div>
                )}
              </div>

              {/* Step 2: Manage Triggers */}
              <div className={`p-4 ${['APPROVED','CONFIRMED'].includes(po.status) ? 'bg-blue-50' : 'bg-white'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    ['APPROVED','CONFIRMED'].includes(po.status) ? 'bg-blue-500 text-white' :
                    ['ACTIVE','COMPLETED'].includes(po.status) ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-400'
                  }`}>
                    {['ACTIVE','COMPLETED'].includes(po.status) ? '✓' : '2'}
                  </div>
                  <span className={`text-sm font-semibold ${['PENDING','DRAFT'].includes(po.status) ? 'text-gray-400' : 'text-gray-700'}`}>
                    Manage Triggers
                  </span>
                </div>
                <p className={`text-xs mb-3 ${['PENDING','DRAFT'].includes(po.status) ? 'text-gray-300' : 'text-gray-500'}`}>
                  Delivery, milestone and time-period triggers are managed from the <strong>Milestones</strong> tab. Approve each to raise invoices.
                </p>
                {['APPROVED','CONFIRMED','ACTIVE'].includes(po.status) ? (
                  <button onClick={() => navigate('/milestones')}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors">
                    <ArrowRight size={14}/> Go to Milestones
                  </button>
                ) : (
                  <div className="text-xs text-gray-300">Approve Sales Order first</div>
                )}
              </div>

              {/* Step 3: Invoices & Payment */}
              <div className={`p-4 ${['APPROVED','CONFIRMED','ACTIVE'].includes(po.status) ? 'bg-green-50' : 'bg-white'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    ['APPROVED','CONFIRMED','ACTIVE','COMPLETED'].includes(po.status) ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-400'
                  }`}>
                    {po.status === 'COMPLETED' ? '✓' : '3'}
                  </div>
                  <span className={`text-sm font-semibold ${['PENDING','DRAFT'].includes(po.status) ? 'text-gray-400' : 'text-gray-700'}`}>
                    Invoices & Payment
                  </span>
                </div>
                <p className={`text-xs mb-3 ${['PENDING','DRAFT'].includes(po.status) ? 'text-gray-300' : 'text-gray-500'}`}>
                  Invoices raised by triggers appear here. Record payments, set up revenue recognition, or raise unbilled entries.
                </p>
                {['APPROVED','CONFIRMED','ACTIVE'].includes(po.status) && (
                  <button onClick={() => navigate('/invoices')}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors">
                    <ArrowRight size={14}/> View Invoices
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Terms Section */}
      <div className="card">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-primary-500"/>
            <h3 className="font-semibold">Payment Terms</h3>
          </div>
          {!hasTerm && (
            <button onClick={() => setShowAddTerm(true)} className="btn-primary text-sm">
              <Plus size={14}/> Add Payment Terms
            </button>
          )}
        </div>

        {!hasTerm ? (
          /* No term yet — show prominent CTA */
          <div className="p-8 text-center">
            <CreditCard size={36} className="mx-auto text-gray-300 mb-3"/>
            <p className="font-medium text-gray-600">No payment terms defined yet</p>
            <p className="text-sm text-gray-400 mt-1 mb-4">
              Define how and when this Sales Order will be paid — advance, milestone splits, deferred, or subscriptions.
            </p>
            <button onClick={() => setShowAddTerm(true)} className="btn-primary mx-auto">
              <Plus size={15}/> Add Payment Terms to this Sales Order
            </button>
          </div>
        ) : (
          /* Show linked terms */
          <div className="divide-y divide-gray-100">
            {terms.map(term => (
              <div key={term.term_id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50">
                <div>
                  <div className="font-medium">{term.name}</div>
                  <div className="text-sm text-gray-500 mt-0.5 flex flex-wrap items-center gap-1">
                    <span>{term.term_type?.replace(/_/g, ' ')} ·</span>
                    <StatusBadge status={term.status} />
                    {term.term_type === 'SUBSCRIPTION' && term.renewal_mode && (
                      <span className="text-xs text-blue-600 font-medium">
                        · {term.renewal_mode === 'AUTO' ? 'Auto Renew'
                          : term.renewal_mode === 'BUYER_APPROVAL_REQUIRED' ? 'Manual Approval'
                          : 'Manual Renewal'}
                      </span>
                    )}
                    {term.term_type === 'SUBSCRIPTION' && !term.renewal_mode && (
                      <span className="text-xs text-amber-600">· Renewal not configured</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/payment-terms/${term.term_id}`)}
                  className="btn-secondary text-sm"
                >
                  View <ArrowRight size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {shouldShowRevenueSchedule(revenueSchedule, editability) && (
        <RevenueRecognitionCard
          schedule={revenueSchedule}
          currency={po.currency}
          onRefresh={load}
          canPost={isFinance || isHeadAccounts || isAdmin}
        />
      )}

      {hasSchedule && (
        <PaymentScheduleCard
          schedule={schedule}
          poId={id}
          poCurrency={po.currency}
          poConversionRate={po.conversion_rate}
          poStatus={po.status}
          canRaiseInvoice={canRaiseInvoice}
          onRefresh={load}
          navigate={navigate}
        />
      )}

      {/* Add Payment Term Modal */}
      {showAddTerm && (
        <TermFormModal
          preselectedPOId={id}
          onClose={() => setShowAddTerm(false)}
          onCreated={(termId) => {
            setShowAddTerm(false);
            load();
            toast.success('Payment term added and linked to this Sales Order');
          }}
        />
      )}

      {showEdit && (
        <EditSOModal
          po={po}
          terms={terms}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); }}
        />
      )}

      {showRevise && (
        <SoModificationWizard
          po={po}
          terms={terms}
          catchupApplicable={Boolean(editability?.catchup_applicable)}
          onClose={() => setShowRevise(false)}
          onSaved={(newPoId) => {
            setShowRevise(false);
            if (newPoId) navigate(`/purchase-orders/${newPoId}`);
            else load();
          }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Cancel Sales Order?"
          message="This will cancel the SO (audit retained) and sync cancellation to O2C. Only allowed when no invoice, revenue, or unbilled postings exist."
          confirmLabel="Cancel SO"
          loading={deleteLoading}
          danger
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}

// ── SO FAMILY CREDIT NOTES (from modifications) ─────────────────────
function SoFamilyCreditNotesCard({ creditNotes, currency }) {
  const credits = creditNotes.filter(cn => String(cn.note_type || 'CREDIT').toUpperCase() !== 'DEBIT');
  const debits = creditNotes.filter(cn => String(cn.note_type || '').toUpperCase() === 'DEBIT');
  return (
    <div className="card border-orange-200">
      <div className="px-5 py-4 border-b bg-orange-50/50">
        <h3 className="font-semibold text-orange-900">Credit / Debit Notes (SO modification)</h3>
        <p className="text-xs text-gray-600 mt-1">
          Issued when this SO family was modified — approve in O2C to sync to Tally.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {['Number', 'Type', 'Invoice', 'Taxable', 'O2C status', 'Issued'].map(h => (
                <th key={h} className="table-th text-xs">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...credits, ...debits].map(cn => (
              <tr key={cn.credit_note_id} className="hover:bg-gray-50 border-t">
                <td className="table-td font-mono text-xs">{cn.credit_note_number}</td>
                <td className="table-td">{cn.note_type || 'CREDIT'}</td>
                <td className="table-td text-xs">{cn.original_invoice_number}</td>
                <td className="table-td">{formatCurrency(creditNoteTaxableAmount(cn), currency)}</td>
                <td className="table-td text-xs">
                  {!cn.bridge_credit_note_due_sent_at ? (
                    <span className="text-amber-700">PTE only</span>
                  ) : cn.o2c_credit_note_id ? (
                    <span className="text-green-700">{cn.o2c_approval_status || 'Synced'}</span>
                  ) : (
                    <span className="text-blue-700">Pending O2C approval</span>
                  )}
                </td>
                <td className="table-td text-xs text-gray-500">{formatDateTime(cn.issued_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── REVENUE RECOGNITION CARD ──────────────────────────────────────
function RevenueRecognitionCard({ schedule, currency, onRefresh, canPost }) {
  const [posting, setPosting] = useState(null);
  const entries = schedule.entries || [];
  const hasCatchup = entries.some(e => parseFloat(e.catchup_adjustment_amount || 0) > 0.009);
  const totalPosted = entries.reduce((s, e) => s + parseFloat(e.recognised_amount || 0), 0);
  const totalCatchup = entries.reduce((s, e) => s + parseFloat(e.catchup_adjustment_amount || 0), 0);

  const handlePost = async (entry) => {
    const net = parseFloat(entry.recognised_amount || 0);
    if (Math.abs(net) < 0.01) {
      setPosting(entry.period_month);
      try {
        const res = await postRevenueEntry(schedule.schedule_id, { period_month: entry.period_month });
        toast.success(res.data?.message || 'Period complete — no Tally journal required (net revenue is zero)');
        onRefresh?.();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to mark period complete');
      } finally {
        setPosting(null);
      }
      return;
    }
    setPosting(entry.period_month);
    try {
      await postRevenueEntry(schedule.schedule_id, { period_month: entry.period_month });
      toast.success(`Revenue posted — ${formatCurrency(entry.recognised_amount, currency)} sent to O2C/Tally`);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post revenue');
    } finally {
      setPosting(null);
    }
  };

  return (
    <div className="card border-violet-200">
      <div className="px-5 py-4 border-b bg-violet-50/40">
        <h3 className="font-semibold text-violet-900">Revenue Recognition Schedule</h3>
        <p className="text-xs text-gray-600 mt-1">
          {hasCatchup
            ? 'Catch-up applied here — post each period to recognise revenue in O2C/Tally (not via installment invoices).'
            : 'Post each period to recognise revenue in O2C/Tally.'}
          {schedule.apportionment_method && (
            <span className="ml-2 inline-flex px-2 py-0.5 rounded bg-violet-100 text-violet-800 font-medium">
              {schedule.apportionment_method === 'EQUAL_MONTHLY' ? 'Equal monthly' : schedule.apportionment_method.replace(/_/g, ' ')}
            </span>
          )}
        </p>
        {hasCatchup && (
          <p className="text-xs text-violet-800 mt-1 font-medium">
            Net to recognise: {formatCurrency(totalPosted, currency)}
            {' '}(catch-up adjustment: −{formatCurrency(totalCatchup, currency)})
          </p>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              {[...(canPost ? ['Month', 'Normal Revenue', 'Catch-up Adjustment', 'Revenue Posted', 'Status', 'Action']
                : ['Month', 'Normal Revenue', 'Catch-up Adjustment', 'Revenue Posted', 'Status'])].map(h => (
                <th key={h} className="table-th text-xs">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => {
              const catchup = parseFloat(entry.catchup_adjustment_amount || 0);
              const net = parseFloat(entry.recognised_amount || 0);
              const normal = catchup > 0.009 ? Math.round((net + catchup) * 100) / 100 : net;
              return (
                <tr key={entry.entry_id} className={
                  entry.status === 'POSTED' ? 'bg-green-50/30' : 'hover:bg-gray-50'
                }>
                  <td className="table-td text-sm font-medium">{entry.period_label}</td>
                  <td className="table-td text-sm">{formatCurrency(normal, currency)}</td>
                  <td className="table-td text-sm text-violet-700">
                    {catchup > 0.009 ? `−${formatCurrency(catchup, currency)}` : '—'}
                  </td>
                  <td className="table-td text-sm font-semibold">{formatCurrency(net, currency)}</td>
                  <td className="table-td">
                    {entry.status === 'POSTED' ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                        <CheckCircle2 size={12}/> Posted
                      </span>
                    ) : (
                      <StatusBadge status={entry.status} />
                    )}
                  </td>
                  {canPost && (
                    <td className="table-td">
                      {entry.status === 'PENDING' && (
                        Math.abs(net) < 0.01 ? (
                          <button
                            type="button"
                            onClick={() => handlePost(entry)}
                            disabled={posting === entry.period_month}
                            className="text-xs px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 border border-gray-200 font-medium"
                            title="Catch-up nets to zero — mark complete without a Tally journal"
                          >
                            {posting === entry.period_month ? 'Saving…' : 'Mark complete'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handlePost(entry)}
                            disabled={posting === entry.period_month}
                            className="text-xs px-2.5 py-1 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200 font-medium"
                          >
                            {posting === entry.period_month ? 'Posting…' : 'Post'}
                          </button>
                        )
                      )}
                      {entry.status === 'POSTED' && Math.abs(net) < 0.01 && !entry.journal_entry_id && (
                        <span className="text-xs text-gray-500 italic">No journal</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── PAYMENT SCHEDULE CARD ─────────────────────────────────────────
function PaymentScheduleCard({ schedule, poId, poCurrency, poConversionRate, poStatus, canRaiseInvoice, onRefresh, navigate }) {
  const [triggering, setTriggering]     = useState(null);
  const [confirmInst, setConfirmInst]   = useState(null);
  const [unbilledInst, setUnbilledInst] = useState(null);
  const [reason, setReason]             = useState('');
  const [invoiceCurrency, setInvoiceCurrency] = useState('');
  const [exchangeRate, setExchangeRate] = useState('');
  const [poBalance, setPOBalance]       = useState(null);
  const [unbilledForm, setUnbilledForm] = useState({
    service_period_from: '',
    service_period_to: '',
    expected_invoice_date: '',
    notes: '',
    apportionment_method: 'EQUAL_MONTHLY',
    day_count_convention: 'ACTUAL_ACTUAL',
  });
  const [savingUnbilled, setSavingUnbilled] = useState(false);

  // PO must be APPROVED/CONFIRMED or ACTIVE before invoices/unbilled can be raised
  const poApproved = ['APPROVED','CONFIRMED','ACTIVE','COMPLETED'].includes(poStatus);

  // Load PO balance whenever unbilled modal opens
  useEffect(() => {
    if (unbilledInst) {
      setPOBalance(null);
      getPOBalance(poId)
        .then(r => setPOBalance(r.data))
        .catch(() => setPOBalance(null));
    }
  }, [unbilledInst, poId]);

  const canForce = (inst) =>
    poApproved && !inst.invoice_id && !['PAID','CANCELLED','WAIVED'].includes(inst.status);

  const handleForce = async () => {
    if (!confirmInst) return;
    setTriggering(confirmInst.installment_id);
    try {
      const payload = { reason: reason || 'Manually raised by user' };
      const invCcy = (invoiceCurrency || poCurrency || confirmInst.currency || 'INR').toUpperCase();
      const baseCcy = (poCurrency || confirmInst.currency || 'INR').toUpperCase();
      if (invCcy !== baseCcy) {
        payload.invoice_currency = invCcy;
        payload.exchange_rate = parseFloat(exchangeRate);
        payload.foreign_currency_amount = parseFloat(confirmInst.computed_amount);
        if (!payload.exchange_rate || payload.exchange_rate <= 0) {
          toast.error('Exchange rate is required when invoice currency differs from SO currency');
          return;
        }
      }
      await forceInvoice(confirmInst.installment_id, payload);
      toast.success(`Invoice raised for "${confirmInst.label}"`);
      setConfirmInst(null); setReason(''); setInvoiceCurrency(''); setExchangeRate('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to raise invoice');
    } finally {
      setTriggering(null);
    }
  };

  const handleUnbilled = async () => {
    if (!unbilledInst) return;
    if (!unbilledForm.service_period_from || !unbilledForm.service_period_to)
      return toast.error('Service period start and end dates are required');

    // Use available balance as the amount (capped to installment amount)
    const instAmt  = parseFloat(unbilledInst.computed_amount);
    const maxAmt   = poBalance ? Math.min(instAmt, poBalance.available_balance) : instAmt;

    if (poBalance && poBalance.available_balance <= 0) {
      return toast.error('No available balance on this Sales Order. All value has been invoiced or recorded as unbilled.');
    }

    setSavingUnbilled(true);
    try {
      await createUnbilledEntry({
        po_id:          poId,
        installment_id: unbilledInst.installment_id,
        amount:         maxAmt,
        currency:       unbilledInst.currency || poBalance?.currency || 'INR',
        ...unbilledForm,
      });
      toast.success('Unbilled Revenue entry created — revenue recognition schedule set up');
      setUnbilledInst(null);
      setUnbilledForm({ service_period_from: '', service_period_to: '', expected_invoice_date: '', notes: '', apportionment_method: 'EQUAL_MONTHLY', day_count_convention: 'ACTUAL_ACTUAL' });
      setPOBalance(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setSavingUnbilled(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold">Payment Schedule</h3>
            <p className="text-xs text-gray-500 mt-0.5">Billing installments — revenue recognition is shown separately below.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>Paid: <strong className="text-green-600">{formatCurrency(schedule.summary?.paid)}</strong></span>
              <span>Outstanding: <strong className="text-amber-600">{formatCurrency(schedule.summary?.outstanding)}</strong></span>
              <span>Total: <strong>{formatCurrency(schedule.summary?.total)}</strong></span>
            </div>
            {/* Process past-due scheduled invoices button */}
            {poApproved && canRaiseInvoice && schedule.installments?.some(i =>
              i.trigger_type === 'TIME_PERIOD' && !i.invoice_id && ['PENDING','DUE'].includes(i.status)
            ) && (
              <ProcessDueButton poId={poId} onDone={onRefresh}/>
            )}
          </div>
        </div>

        {!poApproved && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 flex items-center gap-2">
            <AlertTriangle size={13}/>
            Invoice and Unbilled Revenue actions are disabled until the Sales Order is approved.
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full" style={{ tableLayout: 'fixed', minWidth: 700 }}>
            <colgroup>
              <col style={{ width: 36 }}/>   {/* # */}
              <col/>                          {/* Installment — flexible */}
              <col style={{ width: 150 }}/>  {/* Trigger */}
              <col style={{ width: 120 }}/>  {/* Amount */}
              <col style={{ width: 96 }}/>   {/* Due Date */}
              <col style={{ width: 90 }}/>   {/* Status */}
              <col style={{ width: 130 }}/>  {/* Invoice / Unbilled */}
              <col style={{ width: 130 }}/>  {/* Actions */}
            </colgroup>
            <thead>
              <tr>
                {['#', 'Installment', 'Trigger', 'Amount', 'Due Date', 'Status', 'Invoice / Unbilled', 'Actions'].map(h => (
                  <th key={h} className="table-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedule.installments.map(inst => (
                <tr key={inst.installment_id} className="hover:bg-gray-50">
                  <td className="table-td text-gray-400 text-xs">{inst.sequence_no}</td>
                  <td className="table-td font-medium">{inst.label}</td>
                  <td className="table-td">
                    <span style={{
                      display: 'inline-block',
                      background: 'var(--bt-gray-100)',
                      color: 'var(--bt-gray-600)',
                      fontSize: 11,
                      fontWeight: 500,
                      padding: '2px 7px',
                      borderRadius: 4,
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.01em',
                    }}>
                      {inst.trigger_type?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="table-td font-semibold">{formatCurrencyDual(inst.computed_amount, inst.currency || poCurrency, poConversionRate)}</td>
                  <td className="table-td text-sm text-gray-500">{formatDate(inst.due_date) || '—'}</td>
                  <td className="table-td"><StatusBadge status={inst.status} /></td>
                  <td className="table-td text-xs text-primary-600 font-mono">
                    {inst.invoice_number
                      ? <button onClick={() => navigate(`/invoices/${inst.invoice_id}`)}
                          className="hover:underline font-mono">{inst.invoice_number}</button>
                      : inst.unbilled_id
                      ? <button onClick={() => navigate(`/unbilled-revenue/${inst.unbilled_id}`)}
                          className="text-blue-600 hover:underline">Unbilled ↗</button>
                      : <span className="text-gray-300">—</span>
                    }
                  </td>
                  <td className="table-td">
                    {/* If unbilled entry already exists → show Raise Invoice button only */}
                    {inst.unbilled_id && !inst.invoice_id && poApproved && !['PAID','CANCELLED','WAIVED'].includes(inst.status) && (
                      <button
                        onClick={() => { setConfirmInst(inst); setReason(''); }}
                        disabled={triggering === inst.installment_id}
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 font-medium"
                        title="Raise customer invoice — converts Unbilled Revenue to Trade Receivable (Step O)"
                      >
                        <Zap size={11}/>
                        Raise Invoice
                      </button>
                    )}
                    {/* No unbilled entry — show both Invoice and Unbilled options */}
                    {!inst.unbilled_id && canForce(inst) && (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => { setConfirmInst(inst); setReason(''); }}
                          disabled={triggering === inst.installment_id}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 font-medium"
                          title="Raise customer invoice directly"
                        >
                          <Zap size={11}/> Invoice
                        </button>
                        <button
                          onClick={() => {
                            setUnbilledInst(inst);
                            setUnbilledForm({ service_period_from: '', service_period_to: '', expected_invoice_date: '', notes: '', apportionment_method: 'EQUAL_MONTHLY', day_count_convention: 'ACTUAL_ACTUAL' });
                          }}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 font-medium"
                          title="Record as Unbilled Revenue — work done, invoice later"
                        >
                          <FileText size={11}/> Unbilled
                        </button>
                      </div>
                    )}
                    {/* Already invoiced or paid */}
                    {inst.invoice_id && <span className="text-xs text-gray-300">—</span>}
                    {!inst.unbilled_id && !canForce(inst) && !inst.invoice_id && <span className="text-xs text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Force Invoice Confirm Modal */}
      {confirmInst && (
        <AppModal
          title={confirmInst?.unbilled_id ? 'Raise Customer Invoice (Convert Unbilled)' : 'Raise Invoice Early?'}
          onClose={() => { setConfirmInst(null); setReason(''); setInvoiceCurrency(''); setExchangeRate(''); }}
          maxWidth={480}
          footer={(
            <>
              <button type="button" onClick={() => { setConfirmInst(null); setReason(''); setInvoiceCurrency(''); setExchangeRate(''); }} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="button" onClick={handleForce} disabled={!!triggering}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium text-sm">
                <Zap size={14}/> {triggering ? 'Raising…' : 'Yes, Raise Invoice'}
              </button>
            </>
          )}
        >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-amber-600"/>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  {confirmInst?.unbilled_id ? (
                    <>
                      Converts the existing <strong>Unbilled Revenue</strong> entry into a <strong>customer invoice</strong> for{' '}
                      <strong>{confirmInst?.label}</strong> ({formatCurrency(confirmInst?.computed_amount, confirmInst?.currency)}).
                      The revenue recognition schedule already set up via the unbilled entry is retained.
                    </>
                  ) : (
                    <>
                      Immediately generates a <strong>customer-facing invoice</strong> for <strong>{confirmInst?.label}</strong> ({formatCurrency(confirmInst?.computed_amount, confirmInst?.currency)}) without waiting for the <strong>{confirmInst?.trigger_type?.replace(/_/g, ' ')}</strong> condition.
                    </>
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {confirmInst?.unbilled_id
                    ? 'Journal: Dr. Customer A/c / Cr. Unbilled Revenue A/c (Step O)'
                    : 'Journal: Dr. Customer A/c / Cr. Deferred Revenue'
                  }
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="label">Invoice Currency</label>
                <select
                  className="select w-full"
                  value={invoiceCurrency || poCurrency || confirmInst?.currency || 'INR'}
                  onChange={e => setInvoiceCurrency(e.target.value)}
                >
                  {['INR', 'USD', 'EUR', 'GBP', 'AED'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              {(invoiceCurrency || poCurrency || 'INR').toUpperCase() !== (poCurrency || confirmInst?.currency || 'INR').toUpperCase() && (
                <div>
                  <label className="label">Exchange Rate</label>
                  <input
                    type="number"
                    step="0.000001"
                    min="0"
                    className="input"
                    value={exchangeRate}
                    onChange={e => setExchangeRate(e.target.value)}
                    placeholder="e.g. 83.5"
                  />
                </div>
              )}
            </div>
            {(invoiceCurrency || poCurrency || 'INR').toUpperCase() !== (poCurrency || confirmInst?.currency || 'INR').toUpperCase()
              && exchangeRate && parseFloat(exchangeRate) > 0 && (
              <p className="text-xs text-gray-500 mb-3">
                Foreign: {formatCurrency(confirmInst?.computed_amount, invoiceCurrency || poCurrency)} →{' '}
                Base: {formatCurrency(parseFloat(confirmInst?.computed_amount) * parseFloat(exchangeRate), poCurrency || 'INR')}
              </p>
            )}
            <div>
              <label className="label">Reason <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="input" value={reason} onChange={e => setReason(e.target.value)}
                placeholder="e.g. Customer requested early invoicing" autoFocus/>
            </div>
        </AppModal>
      )}

      {/* Unbilled Revenue Modal */}
      {unbilledInst && (
        <AppModal
          title="Record as Unbilled Revenue"
          subtitle={`${unbilledInst.label} — ${formatCurrency(unbilledInst.computed_amount, unbilledInst.currency)}`}
          onClose={() => { setUnbilledInst(null); setPOBalance(null); }}
          maxWidth={512}
          footer={(
            <>
              <button type="button" onClick={() => { setUnbilledInst(null); setPOBalance(null); }}
                className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="button" onClick={handleUnbilled}
                disabled={savingUnbilled || !poBalance || poBalance.available_balance <= 0}
                className="btn-primary flex-1 justify-center">
                <FileText size={14}/>
                {savingUnbilled ? 'Recording…' : `Record ${poBalance ? formatCurrency(Math.min(parseFloat(unbilledInst.computed_amount), poBalance.available_balance), poBalance.currency) : ''}`}
              </button>
            </>
          )}
        >

            {/* SO Balance breakdown */}
            {!poBalance ? (
              <div className="mb-4 p-3 bg-gray-50 rounded-xl text-xs text-gray-400 animate-pulse">Loading SO balance…</div>
            ) : (
              <div className="mb-4 rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  SO Balance — {poBalance.po_number}
                </div>
                <div className="grid grid-cols-3 divide-x divide-gray-100">
                  <div className="px-3 py-2.5 text-center">
                    <div className="text-xs text-gray-400">SO Value</div>
                    <div className="font-semibold text-sm mt-0.5">{formatCurrency(poBalance.po_value, poBalance.currency)}</div>
                  </div>
                  <div className="px-3 py-2.5 text-center">
                    <div className="text-xs text-gray-400">Committed</div>
                    <div className="font-semibold text-sm mt-0.5 text-amber-600">
                      {formatCurrency(poBalance.committed_amount, poBalance.currency)}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {formatCurrency(poBalance.invoiced_amount)} inv + {formatCurrency(poBalance.unbilled_amount)} unb
                    </div>
                  </div>
                  <div className="px-3 py-2.5 text-center">
                    <div className="text-xs text-gray-400">Available</div>
                    <div className={`font-bold text-sm mt-0.5 ${poBalance.available_balance > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {formatCurrency(poBalance.available_balance, poBalance.currency)}
                    </div>
                  </div>
                </div>
                {poBalance.available_balance <= 0 && (
                  <div className="px-3 py-2 bg-red-50 border-t border-red-100 text-xs text-red-600">
                    ⚠ No available balance — full SO value is already invoiced or recorded as unbilled revenue
                  </div>
                )}
                {poBalance.available_balance > 0 && parseFloat(unbilledInst.computed_amount) > poBalance.available_balance && (
                  <div className="px-3 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-700">
                    ℹ Installment amount {formatCurrency(unbilledInst.computed_amount)} exceeds available balance.
                    Entry will be recorded for {formatCurrency(poBalance.available_balance)} (the available balance).
                  </div>
                )}
              </div>
            )}

            {/* Accounting summary */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-800 space-y-1">
              <p className="font-medium">Ind AS 115 — Scenario 2 journal entries:</p>
              <p className="font-mono">Step A now: &nbsp; Dr. Unbilled Revenue / Cr. Deferred Revenue</p>
              <p className="font-mono">Steps B–N: &nbsp;&nbsp;Dr. Deferred Revenue / Cr. Revenue (monthly)</p>
              <p className="font-mono">Step O later: Dr. Customer A/c / Cr. Unbilled Revenue</p>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Service Period Start <span className="text-red-500">*</span></label>
                  <input type="date" className="input"
                    value={unbilledForm.service_period_from}
                    onChange={e => setUnbilledForm(f => ({ ...f, service_period_from: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">Service Period End <span className="text-red-500">*</span></label>
                  <input type="date" className="input"
                    value={unbilledForm.service_period_to}
                    onChange={e => setUnbilledForm(f => ({ ...f, service_period_to: e.target.value }))}
                    min={unbilledForm.service_period_from}/>
                </div>
              </div>

              {/* Apportionment — same as Finance → Unbilled Revenue create */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Apportionment</label>
                  <select className="select w-full" value={unbilledForm.apportionment_method}
                    onChange={e => setUnbilledForm(f => ({ ...f, apportionment_method: e.target.value }))}>
                    <option value="EQUAL_MONTHLY">Equal monthly</option>
                    <option value="DAY_COUNT">Day count convention</option>
                  </select>
                </div>
                {unbilledForm.apportionment_method === 'DAY_COUNT' && (
                  <div>
                    <label className="label">Day Count Convention</label>
                    <select className="select w-full" value={unbilledForm.day_count_convention}
                      onChange={e => setUnbilledForm(f => ({ ...f, day_count_convention: e.target.value }))}>
                      <option value="ACTUAL_ACTUAL">Actual / Actual</option>
                      <option value="ACTUAL_365">Actual / 365</option>
                      <option value="ACTUAL_360">Actual / 360</option>
                      <option value="THIRTY_360">30 / 360</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="label">Expected Invoice Date to Customer</label>
                <input type="date" className="input"
                  value={unbilledForm.expected_invoice_date}
                  onChange={e => setUnbilledForm(f => ({ ...f, expected_invoice_date: e.target.value }))}/>
              </div>
              <div>
                <label className="label">Notes</label>
                <input className="input" value={unbilledForm.notes}
                  onChange={e => setUnbilledForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. Milestone complete, billing deferred per customer request"/>
              </div>
            </div>

        </AppModal>
      )}
    </>
  );
}


function EditSOModal({ po, terms, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: po.title || '',
    description: po.description || '',
    po_value: po.po_value || '',
    currency: po.currency || 'INR',
    customer_currency: po.customer_currency || po.currency || 'INR',
    conversion_rate: po.conversion_rate || '',
    customer_id: po.customer_id || '',
    po_date: po.po_date ? String(po.po_date).split('T')[0] : '',
    delivery_date: po.delivery_date ? String(po.delivery_date).split('T')[0] : '',
    notes: po.notes || '',
    invoice_approval_mode: po.invoice_approval_mode || 'DRAFT_FOR_REVIEW',
    change_comment: '',
  });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [termForm, setTermForm] = useState({ name: '', term_type: 'DELIVERY_BASED', description: '' });
  const [installments, setInstallments] = useState([]);
  const [initialTermSnapshot, setInitialTermSnapshot] = useState(null);
  const [ptLoading, setPtLoading] = useState(Boolean(terms[0]?.term_id));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const primaryTerm = terms[0];

  useEffect(() => {
    import('../services/api').then(({ getCustomers }) => {
      getCustomers({ is_active: true })
        .then(c => setCustomers(c.data.customers || []))
        .catch(() => {});
    });
  }, []);

  useEffect(() => {
    if (!primaryTerm?.term_id) return;
    getPaymentTerm(primaryTerm.term_id).then(res => {
      const t = res.data.term;
      setTermForm({ name: t.name, term_type: t.term_type, description: t.description || '' });
      const mapped = (t.installments || []).map(i => ({
        installment_id: i.installment_id,
        invoice_id: i.invoice_id,
        sequence_no: i.sequence_no,
        label: i.label,
        amount_type: i.amount_type,
        amount_value: i.amount_value,
        trigger_type: i.trigger_type,
        anchor_date_type: i.anchor_date_type,
        due_offset_days: i.due_offset_days || 0,
        scheduled_invoice_date: i.scheduled_invoice_date ? String(i.scheduled_invoice_date).split('T')[0] : '',
        grace_period_days: i.grace_period_days || 0,
        requires_buyer_approval: i.requires_buyer_approval || false,
      }));
      setInstallments(mapped);
      setInitialTermSnapshot({
        name: t.name,
        term_type: t.term_type,
        description: t.description || '',
        installments: mapped,
      });
      setPtLoading(false);
    }).catch(() => setPtLoading(false));
  }, [primaryTerm?.term_id]);

  const pctValid = installments.length === 0 || validateInstallmentPct(installments);

  const installmentsLocked = installments.some(i => i.invoice_id);

  const termMetadataChanged = !initialTermSnapshot || (
    termForm.name !== initialTermSnapshot.name
    || termForm.term_type !== initialTermSnapshot.term_type
    || termForm.description !== initialTermSnapshot.description
  );

  const installmentsChanged = !initialTermSnapshot || (
    JSON.stringify(installments) !== JSON.stringify(initialTermSnapshot.installments)
  );

  const paymentTermsChanged = termMetadataChanged
    || (!installmentsLocked && installmentsChanged);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.change_comment?.trim()) {
      return toast.error('Please enter a change comment for audit trail');
    }
    if (primaryTerm && installments.length > 0 && !pctValid) {
      return toast.error('Installments must sum to 100%');
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        po_value: parseFloat(form.po_value),
        conversion_rate: form.conversion_rate ? parseFloat(form.conversion_rate) : undefined,
        update_payment_terms: Boolean(primaryTerm) && paymentTermsChanged,
        payment_term: primaryTerm && paymentTermsChanged ? {
          term_id: primaryTerm.term_id,
          name: termForm.name,
          term_type: termForm.term_type,
          description: termForm.description,
          ...(installmentsLocked ? {} : { installments }),
        } : undefined,
      };
      const res = await updatePO(po.po_id, payload);
      if (res.data.payment_term_warning) {
        toast(res.data.payment_term_warning, { icon: '⚠️' });
      }
      toast.success(res.data.message || 'Sales Order setup saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update Sales Order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppModal title={`Edit SO Setup — ${po.po_number}`} onClose={onClose} maxWidth={768}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
          Pre-approval setup only. After the SO is approved, use <strong>Modify Sales Order</strong> for commercial changes.
        </p>
        <div>
          <label className="label">Change comment <span className="text-red-500">*</span></label>
          <input className="input" value={form.change_comment} required
            placeholder="Why is this SO being modified? (audit trail)"
            onChange={e => set('change_comment', e.target.value)} />
        </div>
        <div>
          <label className="label">Customer</label>
          <select className="select" value={form.customer_id || ''}
            onChange={e => set('customer_id', e.target.value)}>
            <option value="">— Select customer —</option>
            {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">SO Title</label>
          <input className="input" value={form.title} onChange={e => set('title', e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">SO Value</label>
            <input type="number" step="0.01" className="input" value={form.po_value}
              onChange={e => set('po_value', e.target.value)} required />
          </div>
          <div>
            <label className="label">Currency</label>
            <select className="select" value={form.currency}
              onChange={e => set('currency', e.target.value)}>
              {['INR', 'USD', 'EUR', 'GBP', 'MYR', 'SGD', 'AED'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {form.currency !== 'INR' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Customer currency (O2C invoice)</label>
              <select className="select" value={form.customer_currency}
                onChange={e => set('customer_currency', e.target.value)}>
                {['INR', 'USD', 'EUR', 'GBP', 'MYR', 'SGD', 'AED'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label">INR conversion rate (1 {form.currency} = ? INR)</label>
              <input type="number" step="0.0001" className="input" value={form.conversion_rate}
                onChange={e => set('conversion_rate', e.target.value)} placeholder="e.g. 83.5" />
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">SO Date</label>
            <input type="date" className="input" value={form.po_date} onChange={e => set('po_date', e.target.value)} />
          </div>
          <div>
            <label className="label">Delivery Date</label>
            <input type="date" className="input" value={form.delivery_date} onChange={e => set('delivery_date', e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Description</label>
          <textarea className="input" rows={2} value={form.description || ''} onChange={e => set('description', e.target.value)} />
        </div>

        {primaryTerm && (
          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="text-sm font-medium text-gray-700">Payment Terms</div>
            {ptLoading ? (
              <div className="text-sm text-gray-400">Loading payment terms…</div>
            ) : (
              <>
                <input className="input" value={termForm.name} placeholder="Payment term name"
                  onChange={e => setTermForm(t => ({ ...t, name: e.target.value }))} />
                <div>
                  <label className="label">Term Type</label>
                  <select className="select" value={termForm.term_type}
                    onChange={e => setTermForm(t => ({ ...t, term_type: e.target.value }))}>
                    {TERM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <InstallmentEditor
                  installments={installments}
                  setInstallments={setInstallments}
                  currency={form.currency}
                  showPresets
                  maxHeight="40vh"
                />
              </>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Saving…' : 'Save Setup'}
          </button>
        </div>
      </form>
    </AppModal>
  );
}

function CreatePOModal({ onClose, onCreated }) {
  const [form, setForm]           = useState({
    title: '', po_value: '', currency: 'INR', customer_currency: 'INR', conversion_rate: '',
    description: '', invoice_approval_mode: 'DRAFT_FOR_REVIEW',
    po_date: new Date().toISOString().split('T')[0],
  });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [soCopy, setSoCopy]       = useState(null);
  const fileRef = React.useRef(null);

  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  useEffect(() => {
    import('../services/api').then(({ getCustomers }) => {
      getCustomers({ is_active: true })
        .then(c => setCustomers(c.data.customers || []))
        .catch(() => {});
    });
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be under 5MB');
      e.target.value = '';
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type) && !/\.(pdf|jpe?g|png|webp|gif)$/i.test(file.name)) {
      toast.error('Please upload a PDF or image file');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSoCopy({
        file_name: file.name,
        file_type: file.type || 'application/pdf',
        file_size: file.size,
        file_data: ev.target.result,
        preview: file.type.startsWith('image/') ? ev.target.result : null,
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCustomerChange = (customerId) => {
    set('customer_id', customerId);
    const cust = customers.find(c => c.customer_id === customerId);
    if (cust?.currency) {
      setForm(f => ({
        ...f,
        customer_id: customerId,
        currency: cust.currency,
        customer_currency: cust.currency,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error('Please select a customer');
    setLoading(true);
    try {
      const payload = {
        ...form,
        po_value: parseFloat(form.po_value),
        conversion_rate: form.conversion_rate ? parseFloat(form.conversion_rate) : undefined,
        customer_currency: form.customer_currency || form.currency,
      };
      const res = await createPO(payload);
      const poId = res.data.po.po_id;
      if (soCopy) {
        await uploadAttachment({
          entity_type: 'purchase_order',
          entity_id: poId,
          file_name: soCopy.file_name,
          file_type: soCopy.file_type,
          file_size: soCopy.file_size,
          file_data: soCopy.file_data,
        });
        await syncSoBridge(poId).catch(() => {});
      }
      toast.success(soCopy ? 'Sales Order created with customer PO' : 'Sales Order created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create Sales Order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppModal title="Create Sales Order" onClose={onClose} maxWidth={672}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Customer (Buyer) <span className="text-red-500">*</span></label>
            <select className="select" value={form.customer_id || ''} onChange={e => handleCustomerChange(e.target.value)} required>
              <option value="">— Select customer —</option>
              {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
            </select>
            {customers.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No customers yet. Go to Settings → Customers first.</p>
            )}
          </div>
          {/* Seller org is automatically set to the logged-in user's organisation */}

          <div>
            <label className="label">SO Title <span className="text-red-500">*</span></label>
            <input className="input" value={form.title} onChange={e => set('title', e.target.value)} required
              placeholder="e.g. Software Development Services Q1 2026"/>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="label">SO Value <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" min="0.01" className="input" value={form.po_value}
                onChange={e => set('po_value', e.target.value)} required placeholder="0.00"/>
            </div>
            <div>
              <label className="label">Currency</label>
              <select className="select" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label">SO Date</label>
              <input type="date" className="input" value={form.po_date} onChange={e => set('po_date', e.target.value)}/>
            </div>
          </div>

          {form.currency !== 'INR' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Customer currency (O2C invoice)</label>
                <select className="select" value={form.customer_currency}
                  onChange={e => set('customer_currency', e.target.value)}>
                  {['INR', 'USD', 'EUR', 'GBP', 'MYR', 'SGD', 'AED'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">INR rate (1 {form.currency} = ? INR)</label>
                <input type="number" step="0.0001" className="input" value={form.conversion_rate}
                  onChange={e => set('conversion_rate', e.target.value)} placeholder="e.g. 83.5" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Invoice Approval Mode</label>
              <select className="select" value={form.invoice_approval_mode} onChange={e => set('invoice_approval_mode', e.target.value)}>
                <option value="AUTO_APPROVE">Auto Approve</option>
                <option value="DRAFT_FOR_REVIEW">Draft for Review</option>
                <option value="DRAFT_WITH_SLA">Draft with SLA</option>
              </select>
            </div>
            <div>
              <label className="label">Expected Delivery Date</label>
              <input type="date" className="input" value={form.delivery_date || ''} onChange={e => set('delivery_date', e.target.value)}/>
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea className="input" rows={2} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Additional notes or reference numbers"/>
          </div>

          <div>
            <label className="label">Customer PO (PDF or image)</label>
            <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden"
              onChange={handleFileSelect}/>
            {!soCopy ? (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed rounded-xl p-4 text-sm text-center transition-colors"
                style={{ borderColor: 'var(--bt-orange-border)', color: 'var(--bt-gray-500)' }}>
                <Upload size={16} className="inline mr-2" style={{ color: 'var(--bt-orange)' }}/>
                Click to attach customer PO (optional)
              </button>
            ) : (
              <div className="flex items-center justify-between gap-3 border rounded-xl p-3"
                style={{ borderColor: 'var(--bt-orange-border)', background: 'var(--bt-orange-light)' }}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} style={{ color: 'var(--bt-orange)' }}/>
                  <span className="text-sm font-medium truncate">{soCopy.file_name}</span>
                </div>
                <button type="button" onClick={() => setSoCopy(null)}
                  className="text-gray-400 hover:text-red-500">
                  <X size={16}/>
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center">
              {loading ? 'Creating…' : 'Create Sales Order'}
            </button>
          </div>
        </form>
    </AppModal>
  );
}

// ── SO Copy Panel (upload / preview / download) ───────────────────
function SOCopyPanel({ po, onUpdated, canUpload }) {
  const [attachment, setAttachment] = React.useState(null);
  const [loading, setLoading]       = React.useState(true);
  const [uploading, setUploading]   = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const fileRef = React.useRef(null);

  const loadAttachment = React.useCallback(() => {
    setLoading(true);
    getAttachments('purchase_order', po.po_id)
      .then(res => setAttachment((res.data.attachments || [])[0] || null))
      .catch(() => setAttachment(null))
      .finally(() => setLoading(false));
  }, [po.po_id]);

  React.useEffect(() => { loadAttachment(); }, [loadAttachment]);

  const decodeFile = async () => {
    const res = await downloadAttachment(attachment.attachment_id);
    const raw = res.data.attachment?.file_data || res.data.file_data;
    const base64 = raw.includes(',') ? raw.split(',')[1] : raw;
    const mime = attachment.file_type || 'application/pdf';
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    return { blob: new Blob([bytes], { type: mime }), mime };
  };

  const handlePreview = async () => {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); return; }
    try {
      const { blob } = await decodeFile();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      toast.error('Failed to load preview');
    }
  };

  const handleDownload = async () => {
    try {
      const { blob } = await decodeFile();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name || `${po.po_number}-customer-po.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download customer PO');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be under 5MB');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      const fileData = await new Promise((resolve, reject) => {
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await uploadAttachment({
        entity_type: 'purchase_order',
        entity_id: po.po_id,
        file_name: file.name,
        file_type: file.type || 'application/pdf',
        file_size: file.size,
        file_data: fileData,
      });
      await syncSoBridge(po.po_id).catch(() => {});
      toast.success('Customer PO uploaded and synced to O2C');
      loadAttachment();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload customer PO');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) return null;

  const isPdf = attachment?.file_type === 'application/pdf' || attachment?.file_name?.endsWith('.pdf');
  const isImage = attachment?.file_type?.startsWith('image/');

  return (
    <div className="rounded-xl border overflow-hidden mb-5" style={{ borderColor: 'var(--bt-orange-border)' }}>
      <div className="flex items-center justify-between px-4 py-3 flex-wrap gap-2"
        style={{ background: 'var(--bt-orange-light)' }}>
        <div className="flex items-center gap-2">
          <FileText size={15} style={{ color: 'var(--bt-orange)' }}/>
          <span className="font-semibold text-sm" style={{ color: 'var(--bt-orange)' }}>Customer PO</span>
          {attachment && (
            <span className="text-xs text-gray-500 truncate max-w-[200px]">{attachment.file_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {attachment && (
            <>
              {(isPdf || isImage) && (
                <button onClick={handlePreview}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium border"
                  style={{ background: '#fff', color: 'var(--bt-orange)', borderColor: 'var(--bt-orange-border)' }}>
                  <FileText size={12}/>
                  {previewUrl ? 'Close Preview' : 'Preview'}
                </button>
              )}
              <button onClick={handleDownload}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{ background: 'var(--bt-orange)', color: '#fff' }}>
                <Download size={12}/> Download
              </button>
            </>
          )}
          {canUpload && (
            <>
              <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleUpload}/>
              <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium border"
                style={{ background: '#fff', color: 'var(--bt-orange)', borderColor: 'var(--bt-orange-border)' }}>
                <Upload size={12}/>
                {uploading ? 'Uploading…' : attachment ? 'Replace' : 'Attach Customer PO'}
              </button>
            </>
          )}
        </div>
      </div>
      {!attachment && (
        <div className="px-4 py-3 text-xs text-gray-500">
          Attach the customer PO document (PDF or image). It will sync to O2C for download from Sales Orders.
        </div>
      )}
      {previewUrl && isPdf && (
        <div style={{ height: 500, background: '#525659' }}>
          <iframe src={previewUrl} title="Customer PO" width="100%" height="100%" style={{ border: 'none' }}/>
        </div>
      )}
      {previewUrl && isImage && (
        <div className="p-4 bg-gray-50 flex justify-center">
          <img src={previewUrl} alt="Customer PO" className="max-h-96 rounded-lg shadow"/>
        </div>
      )}
    </div>
  );
}

// ── Process Past-Due Scheduled Installments Button ───────────────
function ProcessDueButton({ poId, onDone }) {
  const [running, setRunning] = React.useState(false);

  const handle = async () => {
    setRunning(true);
    try {
      const res = await processDueInstallments(poId);
      const { processed, failed, message } = res.data;
      if (processed > 0) {
        toast.success(`${message} — ${processed} draft invoice(s) raised.`);
        onDone();
      } else {
        toast(message || 'No past-due installments found.');
      }
      if (failed > 0) toast.error(`${failed} installment(s) failed to process.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to process installments');
    } finally {
      setRunning(false);
    }
  };

  return (
    <button
      onClick={handle}
      disabled={running}
      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium border"
      style={{
        background: running ? '#FEF3C7' : '#FFFBEB',
        color: '#D97706',
        borderColor: '#FDE68A',
        opacity: running ? 0.8 : 1,
        cursor: running ? 'not-allowed' : 'pointer',
      }}
      title="Immediately raise draft invoices for all past-due Time Period installments on this Sales Order"
    >
      <span>{running ? '⏳' : '⚡'}</span>
      {running ? 'Processing…' : 'Raise Past-Due Invoices'}
    </button>
  );
}
